import { COMPANY, LAST_UPDATED, LEGAL_PATHS, VERIFICATION_URL, supportEmailLink } from './company';

// The consumer sale terms for orders placed through the Marketplace: how the
// contract with the brand is formed, and the shopper's cancellation, return and
// refund rights under UK consumer law.
const content = `
## 1. These terms

These Terms & Conditions of Sale ("**Sale Terms**") apply to every order placed through the ${COMPANY.tradingName} marketplace at ${COMPANY.marketplaceUrl} (the "**Marketplace**"). Please read them before you
order. By placing an order you agree to them.

The Marketplace is operated by ${COMPANY.legalName}, a company registered in ${COMPANY.jurisdiction} and trading as ${COMPANY.tradingName} ("**${COMPANY.tradingName}**", "**we**", "**us**").

Our [Terms of Service](${LEGAL_PATHS.termsOfService}) govern your use of the Marketplace generally,
and our [Privacy Policy](${LEGAL_PATHS.privacyPolicy}) explains how we handle your personal data.

## 2. Who you are buying from

Every product on the Marketplace is sold by the brand shown on the product page (the
"**Seller**"), not by ${COMPANY.tradingName}. When you place an order, a contract of sale is formed
directly between you and that Seller, on these Sale Terms.

${COMPANY.tradingName} operates the Marketplace, verifies the brands on it, takes payment through our
payment provider and supports you if something goes wrong. We are not the seller of the goods, and
we are not the manufacturer or importer.

If your order contains items from more than one Seller, you are entering into a separate contract
with each of them, and items may be dispatched and delivered separately.

## 3. Product information and sustainability claims

We take care to describe products accurately, but product descriptions, ingredients, materials,
photographs and stock levels are provided by the Seller. Photographs and colours are illustrative:
packaging and shades can vary, and handmade or natural products vary by their nature.

The ${COMPANY.tradingName} ecolabels shown against a product reflect the criteria that the brand and
product have been verified against under our standards, on the evidence available to us. They are
not a guarantee about an individual item's performance, and they do not replace the Seller's own
legal responsibility for its claims. You can read how brands and products are verified at
[How brands get verified](${VERIFICATION_URL}).

## 4. Placing an order

- Adding items to your basket and going through checkout is an offer by you to buy those items.
- Your order is accepted, and the contract with the Seller is formed, when we send you an order
  confirmation email. If we cannot accept your order (for example because an item is out of stock,
  because a price or description was wrong, or because payment was declined), we will tell you and
  will not charge you, or will refund you in full.
- Where you buy through a negotiated offer or an accepted quote, the contract is formed on the
  agreed price and terms when the offer or quote is accepted and payment is taken.
- We assign an order number to each order. Please quote it whenever you contact us or the Seller.

Products on the Marketplace are sold for personal, domestic use. We may cancel or limit orders that
appear to be for resale.

## 5. Price and payment

- Prices are in pounds sterling and include VAT where it applies. Delivery charges are shown before
  you confirm your order.
- Payment is taken at the time you place your order, by the card or payment method you choose at
  checkout. Payments are processed by Stripe; we do not store your full card details.
- We take reasonable care to ensure prices are correct. If we find an obvious pricing error before
  your order is accepted, we will contact you to confirm whether you want to continue at the correct
  price, or will cancel and refund the order.
- If VAT rates change between your order and delivery, we will adjust the VAT you pay unless you
  have already paid in full.

## 6. Delivery

- Delivery options, charges and dispatch times are set by each Seller and shown on the product page
  or at checkout.
- Unless a different date is agreed, your order will be delivered within 30 days of the day after
  the contract is formed. If we or the Seller cannot meet that, we will contact you and you may
  choose to wait or to cancel for a full refund.
- Delivery is to the address you give at checkout, and to mainland UK addresses unless the Seller
  states otherwise. Please make sure the address is correct: we cannot always redirect an order once
  it has been dispatched.
- An order is your responsibility from the time it is delivered to the address you gave us. You own
  the goods once we have received payment in full.
- If no one is available to take delivery, the carrier will normally leave a note explaining how to
  rearrange delivery or collect the parcel. If a parcel is returned to the Seller as undelivered
  after reasonable attempts, we will refund the price of the goods but may deduct the cost of the
  failed delivery.
- Delays outside our or the Seller's reasonable control (for example severe weather or carrier
  disruption) do not entitle you to compensation, but we will keep you informed and you may cancel
  if the delay is substantial.

## 7. Your right to change your mind (14 day cancellation)

If you are a consumer in the UK, you have a legal right to cancel most orders under the Consumer
Contracts (Information, Cancellation and Additional Charges) Regulations 2013 without giving a
reason. You have 14 days from the day you (or someone you nominate) receive the goods or, for an
order of several items delivered separately, the last of the items.

**How to cancel:** email ${supportEmailLink} with your order number and the items you are
cancelling, or use any cancellation form the Seller provides. Telling us within the 14 days is
enough. You do not need to have returned the goods by then.

**Returning the goods:** send the goods back within 14 days of telling us you are cancelling. Please
return them in a saleable condition, with any packaging that is needed to protect them. Unless the
Seller states that it pays return postage, you pay the cost of returning the goods. If you handle
the goods more than was necessary to inspect them, we may deduct an amount from your refund to
reflect the reduction in their value.

**Your refund:** we will refund the price you paid, plus the standard delivery charge you paid,
within 14 days of receiving the goods back (or of evidence that you have sent them). If you chose a
faster delivery option, we refund the standard rate. Refunds go back to the payment method you used.

**When the 14 day right does not apply.** By law, you cannot use this right for:

- goods that are sealed for health protection or hygiene reasons, once the seal is broken (for
  example some cosmetics, skincare and intimate products);
- goods that were made or personalised to your specification;
- goods that deteriorate or expire quickly, such as fresh food;
- sealed audio, video or software once unsealed; or
- newspapers and magazines (other than subscriptions).

## 8. Faulty, damaged or incorrect items

Nothing in these Sale Terms affects your legal rights under the Consumer Rights Act 2015. In
summary, goods must be as described, fit for purpose and of satisfactory quality. If they are not:

- **within 30 days** of receiving the goods you can reject them and get a full refund;
- **within six months** you can ask for a repair or replacement, or a full refund if a repair or
  replacement is not possible or fails; and
- **up to six years** (five years in Scotland) you may still be entitled to some money back.

If something arrives damaged, faulty or not what you ordered, email ${supportEmailLink} with your
order number and a photograph if you can. We will sort out a replacement, repair or refund with the
Seller. You will not be charged for returning faulty or incorrect goods.

## 9. Cancellations and changes by us or the Seller

We or the Seller may cancel an order and refund you in full if the item is unavailable, if there was
a material error in the listing, if we cannot verify your payment or address, or if we reasonably
suspect fraud or abuse of these Sale Terms or of our
[Terms of Service](${LEGAL_PATHS.termsOfService}).

If you want to change an order after placing it, email ${supportEmailLink} as soon as you can. We
will do what we can, but once an order has been dispatched a change may not be possible. In that
case you can rely on your cancellation rights above.

## 10. Gift cards, discounts and promotions

Discount codes and promotions may be limited in time, in quantity or to particular products or
customers, may not be combined unless we say so, and have no cash value. We may withdraw or amend a
promotion where it is being abused or where it was published in error.

## 11. Liability

Neither we nor the Seller exclude or limit liability where it would be unlawful to do so. This
includes liability for death or personal injury caused by negligence, for fraud or fraudulent
misrepresentation, for breach of your statutory rights in relation to the goods, or under the
Consumer Protection Act 1987.

Subject to that, we are not liable for loss or damage that was not foreseeable (that is, loss or
damage that was neither obvious nor contemplated by you and us when the contract was formed), nor
for any loss arising from your use of a product otherwise than in accordance with the Seller's
instructions or with applicable safety warnings.

If you are buying as a business rather than as a consumer, our total liability in connection with an
order is limited to the price of that order, and we exclude liability for loss of profit, loss of
business, business interruption and loss of business opportunity.

## 12. Complaints and disputes

If something has gone wrong with an order, please email ${supportEmailLink} with your order number.
We aim to acknowledge complaints within five working days and to resolve them within 14 days,
mediating with the Seller where that is needed.

Nothing here prevents you from taking a claim to court, or from using an alternative dispute
resolution scheme where one is available to you. If you paid by card and cannot resolve a problem
with us or the Seller, you may also be able to ask your card provider about a chargeback.

## 13. Other important terms

- These Sale Terms, together with your order confirmation, form the whole agreement about your
  order.
- If any provision is found to be unenforceable, the rest continue to apply.
- A delay in enforcing a term is not a waiver of it.
- The contract is between you and the Seller (and, where these Sale Terms give us obligations, us).
  No one else has a right to enforce it.

## 14. Governing law

These Sale Terms are governed by the laws of ${COMPANY.jurisdiction} and are subject to the nonexclusive
jurisdiction of the courts of ${COMPANY.jurisdiction}. If you live in Scotland or Northern Ireland you may
also bring proceedings in your local courts.

## 15. Contact

- ${COMPANY.legalName}, trading as ${COMPANY.tradingName}
- Email: ${supportEmailLink}
- Website: [${COMPANY.website}](${COMPANY.website})
`;

const termsAndConditionsOfSale = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms-and-conditions-of-sale',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms & Conditions of Sale' },
      description: { fieldType: 'paragraph', content: `Last updated: ${LAST_UPDATED}` },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'terms-and-conditions-of-sale-content',
          text: { fieldType: 'markdown', content },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: `Terms & Conditions of Sale | ${COMPANY.tradingName}`,
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: `The terms that apply to orders placed through the ${COMPANY.tradingName} marketplace, including delivery, cancellation, returns and refunds.`,
    },
  },
};

export default termsAndConditionsOfSale;
